/**
 * Lešení Český Ráj — Hero ScrollTrigger + Configurator
 * Depends on: gsap, ScrollTrigger (loaded before this file from Hero section)
 */
(function () {
  'use strict';

  if (window.__leseniExperienceBooted) return;
  window.__leseniExperienceBooted = true;

  /**
   * Fallback hodnoty (pokud sekce nepošle JSON).
   * Ostré hodnoty se berou z Theme Editoru: Hero → „Odhalení hotové fasády“.
   * clip-path: inset(TOP% 0 0 0) — čím menší TOP, tím víc fasády odspoda vidět.
   */
  var facadeRevealBreakpoints = [70, 50, 25, 0];

  function loadFacadeBreakpoints() {
    var node = document.querySelector('[data-leseni-facade-breakpoints]');
    if (!node) return facadeRevealBreakpoints.slice();
    try {
      var parsed = JSON.parse(node.textContent);
      if (!Array.isArray(parsed) || parsed.length < 4) return facadeRevealBreakpoints.slice();
      return [
        Number(parsed[0]),
        Number(parsed[1]),
        Number(parsed[2]),
        Number(parsed[3])
      ].map(function (n, i) {
        if (isNaN(n)) return facadeRevealBreakpoints[i];
        return Math.max(0, Math.min(100, n));
      });
    } catch (e) {
      return facadeRevealBreakpoints.slice();
    }
  }

  var DEFAULT_PRICES = {
    service: { rent: 0, assembly: 4500 },
    area: { '0-100': 8900, '100-200': 14900, '200-300': 21900, '300-400': 28900 },
    type: { classic: 0, sliding: 3200 },
    extras: { net: 1800 }
  };

  var AREA_LEVELS = {
    '0-100': 1,
    '100-200': 2,
    '200-300': 3,
    '300-400': 4
  };

  function loadPrices() {
    var node = document.querySelector('[data-leseni-prices]');
    if (!node) return DEFAULT_PRICES;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return DEFAULT_PRICES;
    }
  }

  function formatCzk(value) {
    try {
      return new Intl.NumberFormat('cs-CZ', {
        style: 'currency',
        currency: 'CZK',
        maximumFractionDigits: 0
      }).format(value);
    } catch (e) {
      return value.toLocaleString('cs-CZ') + ' Kč';
    }
  }

  function waitForGsap(cb) {
    if (window.gsap && window.ScrollTrigger) {
      cb(window.gsap, window.ScrollTrigger);
      return;
    }
    var tries = 0;
    var id = setInterval(function () {
      tries += 1;
      if (window.gsap && window.ScrollTrigger) {
        clearInterval(id);
        cb(window.gsap, window.ScrollTrigger);
      } else if (tries > 80) {
        clearInterval(id);
      }
    }, 50);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function dedupeScenes(heroScene, dock) {
    if (!dock || !heroScene) return;
    var fallback = dock.querySelector('[data-leseni-dock-fallback]');
    if (fallback) fallback.remove();
  }

  function setSettled(scaffoldSlots, on) {
    scaffoldSlots.forEach(function (el) {
      if (on) el.classList.add('is-settled');
      else el.classList.remove('is-settled');
    });
  }

  function getScaffoldSlots(scene) {
    return Array.prototype.slice.call(scene.querySelectorAll('[data-scaffold-slot]'));
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function clamp01(v) {
    return Math.max(0, Math.min(1, v));
  }

  function initHero(gsap, ScrollTrigger) {
    var hero = document.querySelector('[data-leseni-hero]');
    var scene = document.querySelector('[data-leseni-hero] [data-leseni-scene]');
    var config = document.querySelector('[data-leseni-config]');
    var dock = document.querySelector('[data-leseni-dock]');
    var progressFill = document.querySelector('[data-leseni-progress-fill]');
    var progressKnob = document.querySelector('[data-leseni-progress-knob]');
    var progressLabels = Array.prototype.slice.call(
      document.querySelectorAll('[data-leseni-progress-label]')
    );

    function setProgressUI(pct) {
      var value = Math.max(0, Math.min(100, pct));
      if (progressFill) progressFill.style.width = value + '%';
      if (progressKnob) progressKnob.style.left = value + '%';
      progressLabels.forEach(function (label) {
        var at = parseFloat(label.getAttribute('data-at'));
        if (isNaN(at)) at = 0;
        label.classList.toggle('is-active', value + 0.001 >= at);
      });
    }

    if (!hero || !scene) return;

    gsap.registerPlugin(ScrollTrigger);
    dedupeScenes(scene, dock);

    var finished = scene.querySelector('[data-layer="finished"]');
    var scaffoldSlots = getScaffoldSlots(scene);
    var heroContent = hero.querySelector('[data-leseni-hero-content]');
    var stage = hero.querySelector('[data-leseni-stage]');
    var heroHeader = hero.querySelector('[data-leseni-hero-header]');
    var configPanel = config ? config.querySelector('.leseni-config__panel') : null;
    var configInner = config ? config.querySelector('.leseni-config__inner') : null;

    var flightFrom = null;
    var flightTo = null;
    var heroRestRect = null;
    var isFlying = false;
    var isDocked = false;
    var handoffActive = false;
    var navJumping = false;
    var heroSizePct = parseFloat(hero.getAttribute('data-leseni-scene-size'));
    var heroOffsetPct = parseFloat(hero.getAttribute('data-leseni-scene-offset-x'));
    if (isNaN(heroSizePct)) heroSizePct = 75;
    if (isNaN(heroOffsetPct)) heroOffsetPct = 0;

    if (prefersReducedMotion()) {
      if (finished) gsap.set(finished, { clipPath: 'inset(0% 0 0 0)' });
      gsap.set(scaffoldSlots, { yPercent: 0 });
      setSettled(scaffoldSlots, true);
      setProgressUI(100);
      if (dock && scene.parentElement !== dock) dock.appendChild(scene);
      scene.classList.add('is-fx-on');
      return;
    }

    gsap.set(scaffoldSlots, { yPercent: -100, force3D: true });
    if (finished) gsap.set(finished, { clipPath: 'inset(100% 0 0 0)' });

    var net = scene.querySelector('[data-layer="net"]');
    if (net) gsap.set(net, { autoAlpha: 0, xPercent: 40 });

    if (configPanel) gsap.set(configPanel, { autoAlpha: 0, y: 28 });
    if (dock) gsap.set(dock, { autoAlpha: 0.15 });

    heroRestRect = captureHeroRect();

    var revealBreakpoints = loadFacadeBreakpoints();
    var buildTl = gsap.timeline({ paused: true });

    scaffoldSlots.forEach(function (slot, i) {
      var floorStart = i * 0.55;
      var revealTop = revealBreakpoints[i] != null ? revealBreakpoints[i] : 0;

      buildTl.to(
        slot,
        {
          yPercent: 0,
          duration: 1,
          ease: 'none',
          force3D: true
        },
        floorStart
      );

      if (finished) {
        buildTl.to(
          finished,
          {
            clipPath: 'inset(' + revealTop + '% 0 0 0)',
            duration: 1,
            ease: 'none'
          },
          floorStart
        );
      }
    });

    var handoffTrigger = configInner || config;
    var reverseAnchor = 1;
    var outboundFromProgress = null;
    var lastFlightDir = 1;
    var UNDOCK_PROGRESS = 0.68;
    var isSnapping = false;
    var snapCooldown = false;

    function copyRect(rect) {
      return {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height
      };
    }

    /** Fallback Hero box from theme settings. */
    function measureHeroRect() {
      var scale = heroSizePct / 100;
      var w = window.innerWidth * scale;
      var h = window.innerHeight * scale;
      return {
        left: (window.innerWidth - w) / 2 + window.innerWidth * (heroOffsetPct / 100),
        top: (window.innerHeight - h) / 2,
        width: w,
        height: h
      };
    }

    function captureHeroRect() {
      if (stage && scene.parentElement === stage && !scene.classList.contains('is-flying')) {
        var live = scene.getBoundingClientRect();
        if (live.width > 1 && live.height > 1) {
          heroRestRect = copyRect(live);
          return copyRect(live);
        }
      }
      if (heroRestRect) return copyRect(heroRestRect);
      return measureHeroRect();
    }

    function measureDockRect() {
      if (!dock) return null;
      var rect = dock.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return null;
      return copyRect(rect);
    }

    /** Dock position when the Ceník block is vertically centered in the viewport. */
    function predictDockRect() {
      var el = configInner || config;
      if (!dock || !el) return measureDockRect();
      var d = dock.getBoundingClientRect();
      var e = el.getBoundingClientRect();
      if (d.width < 1 || d.height < 1) return null;
      return {
        left: d.left,
        top: window.innerHeight / 2 - e.height / 2 + (d.top - e.top),
        width: d.width,
        height: d.height
      };
    }

    function clearSceneInline() {
      scene.classList.remove('is-flying', 'is-flying-to-hero');
      gsap.set(scene, {
        clearProps:
          'transform,x,y,scale,scaleX,scaleY,xPercent,yPercent,position,left,top,width,height,zIndex,margin,maxWidth,maxHeight'
      });
      scene.style.cssText = '';
    }

    function setDockFx(on) {
      if (on) {
        scene.classList.add('is-fx-on');
        scene.classList.remove('is-fx-paused');
        watchFxVisibility();
      } else {
        scene.classList.remove('is-fx-on', 'is-fx-paused');
        if (fxObserver) {
          fxObserver.disconnect();
          fxObserver = null;
        }
      }
    }

    var fxObserver = null;

    function watchFxVisibility() {
      if (fxObserver || typeof IntersectionObserver === 'undefined') return;
      var target = config || scene;
      fxObserver = new IntersectionObserver(
        function (entries) {
          var vis = entries[0] && entries[0].isIntersecting;
          scene.classList.toggle('is-fx-paused', !vis);
        },
        { root: null, rootMargin: '15% 0px', threshold: 0.01 }
      );
      fxObserver.observe(target);
    }

    var heroSection = hero.closest('.shopify-section');

    function setInboundUi(on) {
      hero.classList.toggle('is-handoff-inbound', !!on);
      if (heroSection) heroSection.classList.toggle('is-leseni-handoff-inbound', !!on);
    }

    function flightT(self) {
      var raw = clamp01(self.progress);
      if (self.direction < 0 && reverseAnchor < 0.999) {
        return clamp01(raw / reverseAnchor);
      }
      if (self.direction >= 0 && outboundFromProgress != null) {
        var span = 1 - outboundFromProgress;
        if (span < 0.02) return raw;
        return clamp01((raw - outboundFromProgress) / span);
      }
      return raw;
    }

    function captureFlightRect() {
      if (!scene) return null;
      var r = scene.getBoundingClientRect();
      if (r.width < 1 || r.height < 1) return null;
      return copyRect(r);
    }

    function retargetTowardDock(rawProgress) {
      var live = captureFlightRect();
      var dest = predictDockRect();
      if (!live || !dest) return;
      flightFrom = live;
      flightTo = dest;
      reverseAnchor = 1;
      outboundFromProgress = clamp01(rawProgress);
      applyFlight(0);
    }

    function retargetTowardHero(rawProgress) {
      var live = captureFlightRect();
      var heroRect = heroRestRect || captureHeroRect();
      if (!live || !heroRect) return;
      flightFrom = heroRect;
      flightTo = live;
      reverseAnchor = Math.max(0.08, Math.min(0.95, rawProgress));
      outboundFromProgress = null;
      applyFlight(1);
    }

    function getCenikSnapY() {
      var el = configInner || config;
      if (!el) return window.scrollY;
      var r = el.getBoundingClientRect();
      return Math.round(window.scrollY + r.top + r.height / 2 - window.innerHeight / 2);
    }

    function killScrubTweens() {
      ScrollTrigger.getAll().forEach(function (st) {
        if (typeof st.getTween !== 'function') return;
        var tw = st.getTween();
        if (tw) {
          tw.progress(1);
          tw.kill();
        }
      });
    }

    function snapCenik() {
      if (!config || isSnapping || snapCooldown || navJumping) return;
      if (prefersReducedMotion()) return;

      var y = getCenikSnapY();
      y = Math.max(0, y);
      var dist = Math.abs(window.scrollY - y);

      snapCooldown = true;
      setTimeout(function () {
        snapCooldown = false;
      }, 900);

      if (dist < 6) {
        killScrubTweens();
        return;
      }

      isSnapping = true;
      navJumping = true;
      var start = window.scrollY;
      var delta = y - start;
      var t0 = performance.now();
      var dur = 280;

      function step(now) {
        var p = Math.min(1, (now - t0) / dur);
        var eased = 1 - Math.pow(1 - p, 3);
        window.scrollTo(0, start + delta * eased);
        if (p < 1) {
          requestAnimationFrame(step);
          return;
        }
        window.scrollTo(0, y);
        killScrubTweens();
        isSnapping = false;
        navJumping = false;
      }

      requestAnimationFrame(step);
    }

    function beginUndock(progress) {
      var dockLive = measureDockRect();
      var heroRect = heroRestRect || captureHeroRect();
      if (!dockLive || !heroRect) return;

      reverseAnchor = Math.max(0.08, Math.min(0.95, progress));
      outboundFromProgress = null;
      lastFlightDir = -1;
      setDockFx(false);
      clearSceneInline();
      isDocked = false;
      isFlying = false;
      ensureFlying(heroRect, dockLive);
      scene.classList.add('is-flying-to-hero');
      applyFlight(1);
      setConfigReveal(1);
      setInboundUi(true);
    }

    function fitScale(from, to) {
      if (!from.width || !from.height) return 1;
      return Math.min(to.width / from.width, to.height / from.height);
    }

    function ensureFlying(fromRect, toRect) {
      if (isFlying || isDocked) return false;

      setDockFx(false);
      flightFrom = fromRect || captureHeroRect();
      flightTo = toRect || predictDockRect();
      if (!flightFrom || !flightFrom.width || !flightFrom.height) return false;
      if (!heroRestRect) heroRestRect = copyRect(flightFrom);

      /* Class + pixel size BEFORE moving to body, otherwise .leseni-scene
         (720px / 4:3) flashes the Ceník size for a frame. */
      scene.classList.add('is-flying');
      gsap.set(scene, {
        position: 'fixed',
        left: 0,
        top: 0,
        width: flightFrom.width,
        height: flightFrom.height,
        x: flightFrom.left,
        y: flightFrom.top,
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        transformOrigin: '0 0',
        force3D: true,
        zIndex: 40,
        margin: 0,
        maxWidth: 'none',
        maxHeight: 'none'
      });
      document.body.appendChild(scene);
      isFlying = true;
      return true;
    }

    /**
     * Uniform scale (same Hero crop) along a straight line of centers.
     * Independent width/height lerp changed aspect ratio → instant Ceník
     * size + right-edge cover crop near the dock.
     */
    function applyFlight(t) {
      if (!flightFrom) return;
      var to = flightTo;
      if (!to || !to.width || !to.height) return;

      var progress = clamp01(t);
      var s = lerp(1, fitScale(flightFrom, to), progress);
      var visW = flightFrom.width * s;
      var visH = flightFrom.height * s;
      var fromCX = flightFrom.left + flightFrom.width / 2;
      var fromCY = flightFrom.top + flightFrom.height / 2;
      var toCX = to.left + to.width / 2;
      var toCY = to.top + to.height / 2;

      gsap.set(scene, {
        width: flightFrom.width,
        height: flightFrom.height,
        x: lerp(fromCX, toCX, progress) - visW / 2,
        y: lerp(fromCY, toCY, progress) - visH / 2,
        scale: s,
        scaleX: s,
        scaleY: s,
        force3D: true
      });
    }

    function finalizeDock() {
      if (!dock) return;
      clearSceneInline();
      if (scene.parentElement !== dock) dock.appendChild(scene);
      scene.style.width = '100%';
      scene.style.height = '100%';
      isFlying = false;
      isDocked = true;
      handoffActive = false;
      flightFrom = null;
      flightTo = null;
      reverseAnchor = 1;
      outboundFromProgress = null;
      lastFlightDir = 1;
      setInboundUi(false);

      gsap.set(dock, { autoAlpha: 1 });
      if (configPanel) gsap.set(configPanel, { autoAlpha: 1, y: 0 });
      if (configInner) config.classList.remove('is-transitioning');

      if (window.LeseniExperience && typeof window.LeseniExperience.syncAreaScaffold === 'function') {
        window.LeseniExperience.syncAreaScaffold(false);
      }
      setDockFx(true);
    }

    function returnToStage() {
      setDockFx(false);
      clearSceneInline();
      if (stage && scene.parentElement !== stage) stage.appendChild(scene);
      isFlying = false;
      isDocked = false;
      handoffActive = false;
      flightFrom = null;
      flightTo = null;
      reverseAnchor = 1;
      outboundFromProgress = null;
      lastFlightDir = 1;
      setInboundUi(false);

      if (dock) gsap.set(dock, { autoAlpha: 0.15 });
      if (configPanel) gsap.set(configPanel, { autoAlpha: 0, y: 28 });
    }

    function setConfigReveal(t) {
      if (!config) return;
      var reveal = clamp01(t);
      if (dock) gsap.set(dock, { autoAlpha: 0.15 + reveal * 0.85 });
      if (configPanel) {
        gsap.set(configPanel, {
          autoAlpha: reveal,
          y: 28 * (1 - reveal)
        });
      }
    }

    // Phase 1: pin hero and build scaffolding only (handoff owned by phase 2)
    ScrollTrigger.create({
      trigger: hero,
      start: 'top top',
      end: '+=170%',
      pin: true,
      pinSpacing: true,
      scrub: 0.5,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onUpdate: function (self) {
        if (navJumping) return;
        var p = self.progress;
        buildTl.progress(p);
        setSettled(scaffoldSlots, p >= 1);
        scene.classList.toggle('is-build-done', p >= 1);

        setProgressUI(Math.round(p * 100));

        if (!handoffActive) {
          if (self.direction < 0) {
            if (heroContent) gsap.set(heroContent, { autoAlpha: 1 });
            if (heroHeader) gsap.set(heroHeader, { autoAlpha: 1 });
          } else {
            if (heroContent) {
              var fade = p > 0.78 ? 1 - (p - 0.78) / 0.22 : 1;
              gsap.set(heroContent, { autoAlpha: Math.max(0, fade) });
            }

            if (heroHeader) {
              var headerFade = p > 0.88 ? 1 - (p - 0.88) / 0.12 : 1;
              gsap.set(heroHeader, { autoAlpha: Math.max(0.35, headerFade) });
            }
          }
        }
      },
      onLeave: function () {
        if (navJumping) return;
        if (!handoffActive) {
          if (!isFlying && !isDocked) {
            ensureFlying();
            applyFlight(0);
          }
        }
        if (heroHeader) gsap.set(heroHeader, { autoAlpha: 0.35 });
        if (heroContent) gsap.set(heroContent, { autoAlpha: 0 });
      },
      onEnterBack: function () {
        if (navJumping) return;
        if (!handoffActive && (isDocked || isFlying)) returnToStage();
      }
    });

    // Phase 2: exclusive owner of Hero ↔ Ceník morph
    if (config && dock) {
      ScrollTrigger.create({
        trigger: handoffTrigger,
        start: 'top bottom',
        end: 'center center',
        scrub: 0.5,
        invalidateOnRefresh: true,
        onEnter: function () {
          if (navJumping || isSnapping) return;
          if (buildTl.progress() < 0.999) return;
          handoffActive = true;
          ensureFlying();
        },
        onEnterBack: function () {
          if (navJumping || isSnapping) return;
          handoffActive = true;
          if (isDocked) snapCenik();
        },
        onUpdate: function (self) {
          if (navJumping || isSnapping) return;
          if (buildTl.progress() < 0.999) return;
          handoffActive = true;

          if (isDocked) {
            if (self.direction < 0 && self.progress < UNDOCK_PROGRESS) {
              beginUndock(self.progress);
            }
            return;
          }

          if (!isFlying) ensureFlying();

          var dir = self.direction < 0 ? -1 : 1;
          if (isFlying && dir !== lastFlightDir) {
            if (dir > 0) retargetTowardDock(self.progress);
            else retargetTowardHero(self.progress);
          }
          lastFlightDir = dir;

          var t = flightT(self);
          applyFlight(t);
          setConfigReveal(t);

          if (self.direction < 0) {
            scene.classList.add('is-flying-to-hero');
            setInboundUi(true);
            var ui = t >= 0.32 ? 0 : 1 - t / 0.32;
            if (heroContent) gsap.set(heroContent, { autoAlpha: ui });
            if (heroHeader) gsap.set(heroHeader, { autoAlpha: Math.max(ui, 0.35 * (1 - t)) });
          } else {
            scene.classList.remove('is-flying-to-hero');
            setInboundUi(false);
            if (heroContent) gsap.set(heroContent, { autoAlpha: 0 });
            if (heroHeader) {
              gsap.set(heroHeader, { autoAlpha: Math.max(0, 0.35 * (1 - t)) });
            }
          }
        },
        onLeave: function () {
          if (navJumping || isSnapping) return;
          applyFlight(1);
          finalizeDock();
          reverseAnchor = 1;
          snapCenik();
        },
        onLeaveBack: function () {
          if (navJumping || isSnapping) return;
          if (isFlying || isDocked) {
            if (isFlying) applyFlight(0);
            returnToStage();
            reverseAnchor = 1;
          } else {
            handoffActive = false;
          }
        }
      });
    }

    window.LeseniExperience = window.LeseniExperience || {};
    window.LeseniExperience.toggleNet = function (show) {
      var target = document.querySelector('[data-layer="net"]');
      if (!target || !window.gsap) return;
      if (show) {
        gsap.fromTo(
          target,
          { autoAlpha: 0, xPercent: 45 },
          { autoAlpha: 1, xPercent: 0, duration: 0.85, ease: 'power3.out' }
        );
      } else {
        gsap.to(target, {
          autoAlpha: 0,
          xPercent: 30,
          duration: 0.45,
          ease: 'power2.in'
        });
      }
    };

    function snapForNav(href) {
      if (href === '#hero') {
        returnToStage();
        buildTl.progress(0);
        setSettled(scaffoldSlots, false);
        scene.classList.remove('is-build-done');
        setProgressUI(0);
        if (heroContent) gsap.set(heroContent, { autoAlpha: 1 });
        if (heroHeader) gsap.set(heroHeader, { autoAlpha: 1 });
        handoffActive = false;
        return;
      }

      buildTl.progress(1);
      setSettled(scaffoldSlots, true);
      scene.classList.add('is-build-done');
      setProgressUI(100);
      if (heroContent) gsap.set(heroContent, { autoAlpha: 0 });
      if (heroHeader) gsap.set(heroHeader, { autoAlpha: 0.35 });
      if (!isDocked) finalizeDock();
      handoffActive = false;
    }

    window.LeseniExperience.jumpTo = function (href, target) {
      if (!target) target = document.querySelector(href);
      if (!target) return;

      navJumping = true;
      if (href === '#cenik' || href === '#benefity' || href === '#kontakt') {
        if (href === '#cenik') {
          window.scrollTo(0, Math.max(0, getCenikSnapY()));
        } else {
          target.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
      } else {
        target.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
      killScrubTweens();
      ScrollTrigger.update();
      killScrubTweens();
      snapForNav(href);

      requestAnimationFrame(function () {
        killScrubTweens();
        snapForNav(href);
        navJumping = false;
      });
    };
  }

  function getActiveScene() {
    return (
      document.querySelector('.leseni-scene.is-flying') ||
      document.querySelector('[data-leseni-dock] > [data-leseni-scene]') ||
      document.querySelector('[data-leseni-dock] [data-leseni-scene]') ||
      document.querySelector('[data-leseni-scene]')
    );
  }

  function areaToLevel(areaValue) {
    return AREA_LEVELS[areaValue] || 4;
  }

  function setScaffoldLevel(level, animate) {
    var scene = getActiveScene();
    if (!scene) return;

    var slots = Array.prototype.slice.call(scene.querySelectorAll('[data-scaffold-slot]'));
    if (!slots.length) return;

    var gsap = window.gsap;
    var useMotion = animate !== false && gsap && !prefersReducedMotion();

    slots.forEach(function (slot) {
      var index = parseInt(slot.getAttribute('data-scaffold-index'), 10) || 0;
      var show = index > 0 && index <= level;

      if (show) {
        slot.classList.add('is-settled');
        if (useMotion) {
          gsap.to(slot, {
            yPercent: 0,
            autoAlpha: 1,
            duration: 0.55,
            ease: 'power2.out',
            overwrite: 'auto'
          });
        } else {
          if (gsap) gsap.set(slot, { yPercent: 0, autoAlpha: 1 });
          else {
            slot.style.transform = 'none';
            slot.style.opacity = '1';
          }
        }
      } else {
        slot.classList.remove('is-settled');
        if (useMotion) {
          gsap.to(slot, {
            yPercent: -100,
            autoAlpha: 0,
            duration: 0.45,
            ease: 'power2.in',
            overwrite: 'auto'
          });
        } else {
          if (gsap) gsap.set(slot, { yPercent: -100, autoAlpha: 0 });
          else {
            slot.style.transform = 'translateY(-100%)';
            slot.style.opacity = '0';
          }
        }
      }
    });
  }

  function syncAreaScaffold(form, animate) {
    if (!form) return;
    var areaInput = form.querySelector('[name="lr_area"]:checked');
    var areaValue = areaInput ? areaInput.value : '300-400';
    setScaffoldLevel(areaToLevel(areaValue), animate);
  }

  function canSyncAreaVisual() {
    var scene = getActiveScene();
    if (!scene) return false;
    if (scene.classList.contains('is-flying')) return true;
    if (scene.closest('[data-leseni-dock]')) return true;
    // No hero on page → configurator owns the scene
    if (!document.querySelector('[data-leseni-hero]')) return true;
    return false;
  }

  function calcPrice(form, prices) {
    var service = (form.querySelector('[name="lr_service"]:checked') || {}).value || 'rent';
    var area = (form.querySelector('[name="lr_area"]:checked') || {}).value || '300-400';
    var type = (form.querySelector('[name="lr_type"]:checked') || {}).value || 'classic';
    var net = form.querySelector('[name="lr_net"]');
    var total =
      (prices.service[service] || 0) +
      (prices.area[area] || 0) +
      (prices.type[type] || 0);
    if (net && net.checked) total += prices.extras.net || 0;
    return total;
  }

  function initConfigurator() {
    var root = document.querySelector('[data-leseni-config]');
    if (!root) return;

    var form = root.querySelector('[data-leseni-calc]');
    var priceEl = root.querySelector('[data-leseni-price]');
    if (!form || !priceEl) return;

    var prices = loadPrices();

    function update() {
      var total = calcPrice(form, prices);
      priceEl.textContent = formatCzk(total);
      priceEl.setAttribute('data-value', String(total));
    }

    form.addEventListener('change', function (e) {
      update();
      var target = e.target;
      if (!target) return;

      // Only facade area drives scaffolding visuals for now.
      if (target.name === 'lr_area' && canSyncAreaVisual()) {
        syncAreaScaffold(form, true);
      }

      if (target.name === 'lr_net') {
        if (window.LeseniExperience && typeof window.LeseniExperience.toggleNet === 'function') {
          window.LeseniExperience.toggleNet(target.checked);
        } else {
          var net = document.querySelector('[data-layer="net"]');
          if (!net) return;
          net.style.opacity = target.checked ? '1' : '0';
          net.style.transform = target.checked ? 'none' : 'translateX(30%)';
        }
      }
    });

    update();

    window.LeseniExperience = window.LeseniExperience || {};
    window.LeseniExperience.syncAreaScaffold = function (animate) {
      if (!canSyncAreaVisual()) return;
      syncAreaScaffold(form, animate !== false);
    };
  }

  function prepareStandaloneConfigVisual() {
    var hero = document.querySelector('[data-leseni-hero]');
    var scene = document.querySelector('[data-leseni-dock] [data-leseni-scene]');
    if (!scene) return;
    var finished = scene.querySelector('[data-layer="finished"]');
    if (finished) finished.style.clipPath = 'inset(0% 0 0 0)';

    if (hero) return;

    var form = document.querySelector('[data-leseni-calc]');
    syncAreaScaffold(form, false);
    scene.classList.add('is-fx-on');
  }

  function boot() {
    prepareStandaloneConfigVisual();
    initConfigurator();
    initInstantNav();
    waitForGsap(function (gsap, ScrollTrigger) {
      initHero(gsap, ScrollTrigger);
    });
  }

  function initInstantNav() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest(
        '[data-leseni-nav], a[href="#hero"], a[href="#hero2"], a[href="#cenik"], a[href="#benefity"], a[href="#kontakt"]'
      );
      if (!link) return;

      var href = link.getAttribute('href');
      if (!href || href.charAt(0) !== '#') return;

      var target = document.querySelector(href);
      if (!target) return;

      e.preventDefault();

      var drawer = document.querySelector('header-drawer');
      var drawerDetails = document.getElementById('Details-menu-drawer-container');
      if (
        drawer &&
        drawerDetails &&
        drawerDetails.hasAttribute('open') &&
        typeof drawer.closeMenuDrawer === 'function'
      ) {
        var summary = drawer.querySelector('summary');
        if (summary) drawer.closeMenuDrawer(e, summary);
      }

      if (window.LeseniExperience && typeof window.LeseniExperience.jumpTo === 'function') {
        window.LeseniExperience.jumpTo(href, target);
      } else {
        target.scrollIntoView({ behavior: 'auto', block: 'start' });
      }

      if (history.replaceState) {
        history.replaceState(null, '', href);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
